<script lang="ts" setup>
import { computed, ref, Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
    ArrowRightIcon,
    CheckCircleIcon,
    ExternalLinkIcon,
    ShieldCheckIcon,
} from '@heroicons/vue/solid';

import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';
import { useUiStore } from '@/stores';
import { MetaIntegrationService } from '@/services';
import {
    launchMetaEmbeddedSignup,
    loadMetaSdk,
    MetaSdk,
    MetaSignupMode,
} from '@/services/metaEmbeddedSignup';
import { toast, toastServiceError } from '@/services/core/notification';
import { MetaConnectionStatus, MetaConnectionStatusResult, Permission } from '@/types';
import WhatsAppAssetStatusPanel from './components/WhatsAppAssetStatusPanel.vue';

const { t } = useI18n();
const uiStore = useUiStore();
const router = useRouter();

const status: Ref<MetaConnectionStatus> = ref(null);
const connection = ref<MetaConnectionStatusResult | null>(null);
const isSignupInProgress = ref(false);
const signupMode = ref<MetaSignupMode>('EXISTING_WABA');

const signupModes: MetaSignupMode[] = ['EXISTING_WABA', 'NEW_NUMBER'];
const comingSoonModes = ['coexistence', 'migration'];

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const isConnected = computed(() => status.value === 'connected');
const connectionButtonLabel = computed(() =>
    isConnected.value
        ? t('whatsappMeta.connect.reconnectButton')
        : t('whatsappMeta.connect.button'),
);

const onConnectClick = async () => {
    if (isSignupInProgress.value) return;

    isSignupInProgress.value = true;
    setLoading(true);
    try {
        const { data: embeddedSignup } = await MetaIntegrationService.startWhatsAppConnect();
        const metaSdk: MetaSdk = await loadMetaSdk(embeddedSignup.appId);
        setLoading(false);
        const result = await launchMetaEmbeddedSignup(
            metaSdk,
            embeddedSignup.configId,
            signupMode.value,
        );
        setLoading(true);
        const { data } = await MetaIntegrationService.completeWhatsAppConnect({
            state: embeddedSignup.state,
            ...result,
        });
        connection.value = data;
        status.value = data.status;
        if (
            data.wabaId &&
            data.migrationRequired &&
            window.confirm(t('whatsappMeta.connect.migrationConfirmation'))
        ) {
            const migration = await MetaIntegrationService.migrateWaba(data.wabaId);
            connection.value = migration.data;
            status.value = migration.data.status;
        }
        if (data.wabaId) {
            await router.replace({ name: 'WhatsAppMetaDetail', params: { wabaId: data.wabaId } });
            return;
        }
        toast.success(t('whatsappMeta.connect.previewReady'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
        isSignupInProgress.value = false;
    }
};

const onConfirmConnection = async (connectionId: string) => {
    setLoading(true);
    try {
        const { data } = await MetaIntegrationService.confirmWhatsAppConnect(connectionId);
        connection.value = data;
        status.value = data.status;
        toast.success(t('whatsappMeta.connect.confirmed'));
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ t('core.entity.Integrations') }}
                </h1>
            </template>
        </UiPageHeader>

        <UiPanel no-padding class="overflow-hidden">
            <div
                class="border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-6 sm:px-8 sm:py-8"
            >
                <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex items-start gap-4">
                        <div
                            class="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#25D366] p-3 text-white shadow-sm"
                        >
                            <SVGWhatsApp class="h-full w-full" />
                        </div>
                        <div>
                            <p class="mb-1 text-sm font-medium text-emerald-700">
                                {{ t('whatsappMeta.connect.eyebrow') }}
                            </p>
                            <h2 class="text-2xl font-semibold tracking-tight text-gray-900">
                                {{ t('whatsappMeta.connect.title') }}
                            </h2>
                            <p class="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
                                {{ t('whatsappMeta.connect.description') }}
                            </p>
                        </div>
                    </div>

                    <div
                        class="inline-flex w-fit items-center rounded-full px-3 py-1.5 text-sm font-medium"
                        :class="
                            isConnected
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-600'
                        "
                    >
                        <CheckCircleIcon class="mr-2 h-4 w-4" aria-hidden="true" />
                        {{
                            isConnected
                                ? t('whatsappMeta.connect.statusConnected')
                                : t('whatsappMeta.connect.statusNotConnected')
                        }}
                    </div>
                </div>
            </div>

            <div class="grid divide-y divide-gray-200 lg:grid-cols-5 lg:divide-x lg:divide-y-0">
                <section class="p-5 sm:p-8 lg:col-span-3">
                    <h3 class="text-base font-semibold text-gray-900">
                        {{ t('whatsappMeta.connect.howItWorksTitle') }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{ t('whatsappMeta.connect.howItWorksDescription') }}
                    </p>

                    <ol class="mt-6 space-y-5">
                        <li class="flex gap-4">
                            <span
                                class="bg-primary flex h-7 w-7 flex-none items-center justify-center rounded-full text-sm font-semibold text-white"
                                >1</span
                            >
                            <div>
                                <h4 class="text-sm font-semibold text-gray-800">
                                    {{ t('whatsappMeta.connect.steps.account.title') }}
                                </h4>
                                <p class="mt-1 text-sm leading-5 text-gray-600">
                                    {{ t('whatsappMeta.connect.steps.account.description') }}
                                </p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <span
                                class="bg-primary flex h-7 w-7 flex-none items-center justify-center rounded-full text-sm font-semibold text-white"
                                >2</span
                            >
                            <div>
                                <h4 class="text-sm font-semibold text-gray-800">
                                    {{ t('whatsappMeta.connect.steps.number.title') }}
                                </h4>
                                <p class="mt-1 text-sm leading-5 text-gray-600">
                                    {{ t('whatsappMeta.connect.steps.number.description') }}
                                </p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <span
                                class="bg-primary flex h-7 w-7 flex-none items-center justify-center rounded-full text-sm font-semibold text-white"
                                >3</span
                            >
                            <div>
                                <h4 class="text-sm font-semibold text-gray-800">
                                    {{ t('whatsappMeta.connect.steps.authorize.title') }}
                                </h4>
                                <p class="mt-1 text-sm leading-5 text-gray-600">
                                    {{ t('whatsappMeta.connect.steps.authorize.description') }}
                                </p>
                            </div>
                        </li>
                    </ol>
                </section>

                <aside class="bg-gray-50 p-5 sm:p-8 lg:col-span-2">
                    <fieldset class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <legend class="sr-only">{{ t('whatsappMeta.connect.modeTitle') }}</legend>
                        <h3 class="text-sm font-semibold text-gray-900" aria-hidden="true">
                            {{ t('whatsappMeta.connect.modeTitle') }}
                        </h3>
                        <div class="mt-3 space-y-2">
                            <label
                                v-for="mode in signupModes"
                                :key="mode"
                                class="flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors"
                                :class="
                                    signupMode === mode
                                        ? 'border-primary bg-primary/5'
                                        : 'border-gray-200 hover:bg-gray-50'
                                "
                            >
                                <input
                                    v-model="signupMode"
                                    type="radio"
                                    name="signup-mode"
                                    :value="mode"
                                    :disabled="isSignupInProgress"
                                    class="text-primary focus:ring-primary mt-0.5 h-4 w-4 border-gray-300"
                                />
                                <span>
                                    <span
                                        class="flex items-center gap-2 text-sm font-medium text-gray-900"
                                    >
                                        {{ t(`whatsappMeta.connect.modes.${mode}.title`) }}
                                        <UiBadge
                                            v-if="mode === 'EXISTING_WABA'"
                                            variant="green"
                                            size="xs"
                                        >
                                            {{ t('whatsappMeta.connect.recommended') }}
                                        </UiBadge>
                                    </span>
                                    <span class="mt-0.5 block text-xs leading-5 text-gray-500">
                                        {{ t(`whatsappMeta.connect.modes.${mode}.description`) }}
                                    </span>
                                </span>
                            </label>
                            <div
                                v-for="mode in comingSoonModes"
                                :key="mode"
                                class="flex gap-3 rounded-lg border border-dashed border-gray-200 p-3 text-gray-500"
                                aria-disabled="true"
                            >
                                <span
                                    class="mt-0.5 h-4 w-4 flex-none rounded-full border border-gray-300"
                                />
                                <span>
                                    <span class="flex items-center gap-2 text-sm font-medium">
                                        {{ t(`whatsappMeta.connect.${mode}.title`) }}
                                        <UiBadge variant="gray" size="xs">
                                            {{ t('whatsappMeta.connect.comingSoon') }}
                                        </UiBadge>
                                    </span>
                                    <span class="mt-0.5 block text-xs leading-5">
                                        {{ t(`whatsappMeta.connect.${mode}.description`) }}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </fieldset>
                    <div class="mt-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            <ShieldCheckIcon class="h-5 w-5 text-emerald-600" aria-hidden="true" />
                            {{ t('whatsappMeta.connect.secureTitle') }}
                        </div>
                        <p class="mt-2 text-sm leading-6 text-gray-600">
                            {{ t('whatsappMeta.connect.secureDescription') }}
                        </p>
                    </div>

                    <div class="mt-5 rounded-xl border border-emerald-100 bg-emerald-50 p-5">
                        <h3 class="text-base font-semibold text-gray-900">
                            {{
                                isConnected
                                    ? t('whatsappMeta.connect.connectedTitle')
                                    : t('whatsappMeta.connect.readyTitle')
                            }}
                        </h3>
                        <p class="mt-2 text-sm leading-6 text-gray-600">
                            {{
                                isConnected
                                    ? t('whatsappMeta.connect.connectedDescription')
                                    : t('whatsappMeta.connect.readyDescription')
                            }}
                        </p>
                        <UiButton
                            v-if="$can(Permission.EDIT_INTEGRATION)"
                            variant="primary"
                            text-variant="white"
                            class="mt-5 w-full"
                            :disabled="isSignupInProgress"
                            @click="onConnectClick"
                        >
                            <span>
                                {{
                                    isSignupInProgress
                                        ? t('whatsappMeta.connect.waitingForMeta')
                                        : connectionButtonLabel
                                }}
                            </span>
                            <ArrowRightIcon class="ml-2 h-4 w-4" aria-hidden="true" />
                        </UiButton>
                        <p class="mt-3 flex items-start text-xs leading-5 text-gray-500">
                            <ExternalLinkIcon
                                class="mr-1.5 mt-0.5 h-3.5 w-3.5 flex-none"
                                aria-hidden="true"
                            />
                            {{
                                isSignupInProgress
                                    ? t('whatsappMeta.connect.waitingForMetaDescription')
                                    : t('whatsappMeta.connect.redirectNotice')
                            }}
                        </p>
                    </div>
                </aside>
            </div>
        </UiPanel>

        <WhatsAppAssetStatusPanel
            v-if="connection"
            :connection="connection"
            @confirm="onConfirmConnection"
        />

        <div class="flex justify-end px-4 sm:px-0">
            <UiBackButton :fallbackTo="{ name: 'ListIntegration' }" />
        </div>
    </div>
</template>
